import { addAudioLogEntry } from 'flow/utils/browser-utils';
import ContextStateObserver from './audio-available-observer';
import {
    setOutputDeviceWithIdToElement,
    unsetSinkId,
} from 'flow/utils/media-utils';
import {
    getPreferredAudioOutputId,
    getPreferredRingtoneAudioOutputId,
} from 'flow/utils/browser-utils';

const tryAttachDeviceWithIdToElement = (deviceId, audioElement) => {
    if (!deviceId) {
        addAudioLogEntry(
            'Unable to attach device to audio element; no device id provided',
        );
        return;
    } else if (!audioElement) {
        addAudioLogEntry(
            'Unable to attach device to audio element; audio element provided',
        );
        return;
    }
    setOutputDeviceWithIdToElement(deviceId, audioElement);
};

const AUDIO_OUTPUTS = {
    PRIMARY: 'PRIMARY',
    SECONDARY: 'SECONDARY',
};
class AudioOutput {
    constructor(context, deviceId, logDescription) {
        this.deviceId = deviceId;
        this.logDescription = logDescription;
        this.destination = context.createMediaStreamDestination();
        this.outputElement = new Audio();
        this.outputElement.autoplay = true;
        this.outputElement.loop = true;
        this.outputElement.srcObject = this.destination.stream;
        this.active = false;
        this.setOutputDevice(deviceId);
    }

    setOutputDevice = (deviceId) => {
        this.deviceId = deviceId;
        addAudioLogEntry(
            `AudioOutput.${this.logDescription}: Setting output device ${deviceId}.`,
        );
    };

    getDevice = () => {
        return this.deviceId;
    };

    getNode = () => {
        return this.destination;
    };

    activate = () => {
        addAudioLogEntry(`AudioOutput.${this.logDescription}: activated.`);
        this.active = true;
    };

    deactivate = () => {
        addAudioLogEntry(`AudioOutput.${this.logDescription}: deactivated.`);
        this.active = false;
    };

    play = async () => {
        if (this.active) {
            try {
                tryAttachDeviceWithIdToElement(
                    this.deviceId,
                    this.outputElement,
                );
                await this.outputElement.play();
            } catch (e) {
                addAudioLogEntry(
                    `AudioOutput.${this.logDescription} play error: ${e}.`,
                );
            }
        }
    };

    pause = async () => {
        try {
            this.outputElement.currentTime = 0;
            await this.outputElement.pause();
            unsetSinkId(this.outputElement);
        } catch (e) {
            addAudioLogEntry(
                `AudioOutput.${this.logDescription} play error: ${e}.`,
            );
        }
    };
}
/*
If AudioContext is constantly running it drains battery and blocks computer from system sleep.
Calling resume on audioContext is the only way to know if user has interacted with the browser and sound is playable.
So there is an initialContext for this use that will be suspended as soon as it's successfully resumed. After that point
the main context can be resumed and suspended when needed for a ringtone.
*/
class WrappedAudioContext {
    constructor(primaryDeviceId, secondaryDeviceId) {
        this.manuallyPaused = false;
        this.audioHasBeenInitialized = false;

        try {
            this.initialContext = new (
                window.AudioContext || window.webkitAudioContext
            )();
            this.context = new (
                window.AudioContext || window.webkitAudioContext
            )();
            this.context.suspend();
        } catch (error) {
            addAudioLogEntry(
                `Your browser does not support the audio generator and won't generate any sounds: ${error}`,
            );
        }

        this.outputs = {
            [AUDIO_OUTPUTS.PRIMARY]: new AudioOutput(
                this.context,
                primaryDeviceId,
                AUDIO_OUTPUTS.PRIMARY,
            ),
            [AUDIO_OUTPUTS.SECONDARY]: new AudioOutput(
                this.context,
                secondaryDeviceId,
                AUDIO_OUTPUTS.SECONDARY,
            ),
        };

        this.initialContextStateObserver = new ContextStateObserver(
            this.initialContext,
            true,
        )
            .onContextRunning(() => {
                addAudioLogEntry(
                    'Resumed initial audio context. Audio has now been initialized',
                );
                this.audioHasBeenInitialized = true;

                if (!this.manuallyPaused) {
                    const { PRIMARY, SECONDARY } = this.outputs;

                    PRIMARY.activate();

                    const hasSecondaryDeviceAtAll = !!SECONDARY.getDevice();
                    const firstAndSecondOutputHaveTheSameDevice =
                        PRIMARY.getDevice() === SECONDARY.getDevice();
                    const shouldActivateSecondOutput =
                        hasSecondaryDeviceAtAll &&
                        !firstAndSecondOutputHaveTheSameDevice;
                    if (shouldActivateSecondOutput) {
                        SECONDARY.activate();
                    }
                } else {
                    addAudioLogEntry(
                        'Will not start audio output element for context since it was previously manually paused.',
                    );
                }
            })
            .onContextSuspended(() => {
                addAudioLogEntry('Suspended initial audio context.');
            });

        this.contextStateObserver = new ContextStateObserver(
            this.context,
            false,
        )
            .onContextRunning(() => {
                addAudioLogEntry(
                    'Resumed main audio context. Ringtone audio should now be playable.',
                );
            })
            .onContextSuspended(() => {
                addAudioLogEntry(
                    'Suspended main audio context. Ringtone audio should not be playable.',
                );
            });
    }

    onInitialContextRunning = (callback) => {
        //This check is needed for when media engagement is high in chrome and audio will be initialized at startup.
        //The callback will be called too late in this case, after it's alreday been suspended again.
        if (this.audioHasBeenInitialized) {
            callback();
        } else {
            this.initialContextStateObserver.onContextRunning(callback);
        }
        return this;
    };

    getAudioOutput = (key) => {
        return this.outputs[key];
    };

    getPrimaryOutput = () => {
        return this.getAudioOutput(AUDIO_OUTPUTS.PRIMARY);
    };

    getSecondaryOutput = () => {
        return this.getAudioOutput(AUDIO_OUTPUTS.SECONDARY);
    };
}

export const MainAudioContext = new WrappedAudioContext(
    getPreferredAudioOutputId(),
    getPreferredRingtoneAudioOutputId(),
);
